import { gql } from '@apollo/client';
import { createContext } from 'libs/react';
import { useContext } from 'libs/react';
import { useQuery } from 'libs/react';
import { decode } from 'libs/util/json';
import { Error } from 'components/Error';
import { Case } from 'components/util/Case';
import { AccountFragment } from 'graphql/Account/fragments/AccountFragment';
import { ActionFragment } from 'graphql/Action/fragments/ActionFragment';
import { LanguageFragment } from 'graphql/Language/fragments/LanguageFragment';
import { ModuleFragment } from 'graphql/Module/fragments/ModuleFragment';
import { React } from 'libs/react';
import { useDomain } from 'components/domain/DomainProvider';
import { applyPrimaryColor, resolvePrimaryColor } from 'helpers/primaryColor';
/**
 * @component AccountProvider
 * @since 1.0.0
 */
export function AccountProvider(props) {
    var _a;
    const [data, ready, error] = useQuery(AccountQuery);
    const { options: domainOptions } = useDomain();
    if (data == null) {
        return null;
    }
    const { account } = data;
    const context = {
        id: account.id,
        name: account.name,
        host: account.host,
        href: account.href,
        logo: account.logo,
        icon: account.icon,
        tag: account.tag,
        type: account.type,
        linkedAccounts: account.linkedAccounts,
        configs: account.configs,
        expirationDate: account.expirationDate,
        options: decode(account.options),
        modules: data.modules,
        actions: data.actions,
        languages: data.languages.filter((item) => item.enabled)
    };
    // Account colour first, then the domain's
    applyPrimaryColor(resolvePrimaryColor((_a = context.options) === null || _a === void 0 ? void 0 : _a.color, domainOptions === null || domainOptions === void 0 ? void 0 : domainOptions.color));
    return (React.createElement(AccountContext.Provider, { value: context },
        React.createElement(Case, { is: error },
            React.createElement(Error, { title: "Ooops!", infos: "An error occured while trying to load the account.", picto: "fal bug", error: error })),
        React.createElement(Case, { is: ready }, props.children)));
}
/**
 * @component AccountContext
 * @since 1.0.0
 */
export const AccountContext = createContext({
    id: '',
    name: '',
    host: '',
    href: '',
    tag: '',
    type: '',
    logo: null,
    icon: null,
    languages: [],
    options: {},
    linkedAccounts: [],
    configs: [],
    modules: [],
    expirationDate: null
});
/**
 * @function useAccount
 * @since 1.0.0
 */
export function useAccount() {
    return useContext(AccountContext);
}
/**
 * @function getAccountConfig
 * @since 1.0.0
 */
export function getAccountConfig(name) {
    var _a, _b;
    const account = useAccount();
    const value = (_b = (_a = account.configs) === null || _a === void 0 ? void 0 : _a.find((config) => config.fieldKey === name)) === null || _b === void 0 ? void 0 : _b.value;
    if (value == null) {
        return null;
    }
    return JSON.parse(value).value;
}
/**
 * @const AccountQuery
 * @since 1.0.0
 */
export const AccountQuery = gql `
  query AccountQuery {
    account {
      ...AccountFragment
    }

    modules {
      ...ModuleFragment
    }

    actions {
      ...ActionFragment
    }

    languages {
      ...LanguageFragment
    }
  }

  ${AccountFragment}
  ${ModuleFragment}
  ${ActionFragment}
  ${LanguageFragment}
`;
